import type { FC, ReactNode } from "react";
import { Box, type BoxProps, Column, Row, Text } from "@hightouch/htui";

import {
  DEFAULT_GHOST_SURFACE,
  ghostMessageFade,
} from "src/components/charts/chart-utils";

const GRID_COLUMNS = 6;
const GRID_ROWS = 3;
// One tick per horizontal gridline (top, two internal, bottom).
const Y_AXIS_TICKS = GRID_ROWS + 1;
const X_AXIS_TICKS = 3;

const GRID_CELL_KEYS = Array.from(
  { length: GRID_COLUMNS * GRID_ROWS },
  (_, index) => `cell-${index}`,
);
const Y_AXIS_KEYS = Array.from(
  { length: Y_AXIS_TICKS },
  (_, index) => `y-${index}`,
);
const X_AXIS_KEYS = Array.from(
  { length: X_AXIS_TICKS },
  (_, index) => `x-${index}`,
);

export type ChartEmptyStateProps = {
  message: string;
  description?: ReactNode;
  /** Color token of the surface behind the chart; the message fade matches it. */
  surfaceColor?: string;
} & Omit<BoxProps, "children">;

export const ChartEmptyState: FC<ChartEmptyStateProps> = ({
  message,
  description,
  surfaceColor = DEFAULT_GHOST_SURFACE,
  ...boxProps
}) => (
  <Column width="100%" height="100%" gap={2} {...boxProps}>
    <Row flex={1} minHeight={0} gap={2}>
      <Box position="relative" flex={1} minWidth={0}>
        <Box
          aria-hidden
          position="absolute"
          inset={0}
          display="grid"
          gridTemplateColumns={`repeat(${GRID_COLUMNS}, 1fr)`}
          gridTemplateRows={`repeat(${GRID_ROWS}, 1fr)`}
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="base.divider"
          borderRadius="lg"
          overflow="hidden"
        >
          {GRID_CELL_KEYS.map((key) => (
            <Box
              key={key}
              borderRightWidth="1px"
              borderBottomWidth="1px"
              borderStyle="dashed"
              borderColor="base.divider"
            />
          ))}
        </Box>
        <Column
          position="absolute"
          inset={0}
          align="center"
          justify="center"
          gap={1}
          px={8}
          textAlign="center"
          bgGradient={ghostMessageFade(surfaceColor)}
        >
          <Text size="lg" color="text.disabled">
            {message}
          </Text>
          {description && (
            <Box maxWidth="md">
              <Text as="div" size="sm" color="text.tertiary">
                {description}
              </Text>
            </Box>
          )}
        </Column>
      </Box>
      <Column width={8} flexShrink={0} justify="space-between" align="center">
        {Y_AXIS_KEYS.map((key) => (
          <Box
            key={key}
            width={6}
            height={3}
            bg="base.background"
            borderRadius="sm"
          />
        ))}
      </Column>
    </Row>
    <Row flexShrink={0} gap={2}>
      <Row flex={1} minWidth={0} justify="space-between">
        {X_AXIS_KEYS.map((key) => (
          <Box
            key={key}
            width={10}
            height={3}
            bg="base.background"
            borderRadius="sm"
          />
        ))}
      </Row>
      <Box width={8} flexShrink={0} />
    </Row>
  </Column>
);
